import { Dropdown, Input as AntdInput, Menu, Select, Tooltip } from 'antd';
import Image from 'next/image';
import { useCallback, useEffect, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { PlusSvg, QuestionMarkSvg } from '../../../assets/icons';
import AIImageFeedbackSvg from '../../../assets/icons/AIImageFeedbackSvg';
import ArrowRightSvg from '../../../assets/icons/ArrowRightSvg';
import { CloudSvg } from '../../../assets/icons/CloudSvg';
import DrawingsSvg from '../../../assets/icons/DrawingsSvg';
import FlashSvg from '../../../assets/icons/FlashSvg';
import InstantFeedbackSvg from '../../../assets/icons/InstantFeedbackSvg';
import MinusSvg from '../../../assets/icons/MinusSvg';
import { PollSvg } from '../../../assets/icons/PollSvg';
import QuestionsSvg from '../../../assets/icons/QuestionsSvg';
import SlideHeaderSvg from '../../../assets/icons/SlideHeaderSvg';
import { SlideHeaderText } from '../../../assets/icons/SlideHeaderText';
import { SlideImageTextSvg } from '../../../assets/icons/SlideImageTextSvg';
import { SlideTextImageSvg } from '../../../assets/icons/SlideTextImageSvg';
import TitleImageSvg from '../../../assets/icons/TitleImageSvg';
import VideoActivitySvg from '../../../assets/icons/VideoActivitySvg';
import YoutubeSvg from '../../../assets/icons/YoutubeSvg';
import theme from '../../../assets/theme';
import { useMetrics } from '../../../hooks/useMetrics';
import { SlideType as SlideContentType } from '../../../services/backendService/types';
import { toSubjects, typeToActivityMap } from '../../../utils/creator';
import { Input } from '../../_atoms/_Input';
import { Button } from '../../_atoms/Button';
import { IconWrapper } from '../../_atoms/IconWrapper';
import { BetaFlag } from '../../_molecules/RoundTypeSelector';
import {
  isCuripodText,
  parseSlateStringToText,
  parseStringToSlateObject,
} from '../../SlideComponents/slateUtil';
import { Slide, SlideType } from '../../SlideComponents/slide.types';
import {
  createTextElement,
  retrieveSlideQuestionElement,
  SLIDE_ASPECT,
} from '../../SlideComponents/slideUtil';

const { Option } = Select;

const StyledFormInputDiv = styled.div`
  margin-bottom: 20px;
  max-width: 100%;
  p {
    font-weight: bold;
    color: #282f33;
  }
`;

const TooltipIconWrapper = styled.span`
  margin-left: 10px;
  &:hover {
    cursor: pointer;
  }
  svg {
    height: 16px;
    width: 16px;
    color: ${theme.colors.black};
  }
`;

const TimerWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 10px;
  padding: 0 10px;
  border: 1px solid rgba(40, 47, 51, 0.2);
  border-radius: 5px;
  width: fit-content;
`;
const TimerInputContainer = styled.div`
  font-size: 25px;
  font-weight: bold;
  height: 100%;
  display: flex;
  align-items: center;

  margin-top: auto;
  margin-bottom: auto;
`;
const TimerInput = styled(AntdInput)`
  width: 45px;
  padding: 0;
  text-align: center !important;
  font-size: 30px;
  border: none;
  /* Chrome, Safari, Edge, Opera */
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  input[type='number'] {
    -moz-appearance: textfield;
  }
`;
export const TimerButton = styled(Button)`
  border: solid 1px ${theme.colors.black};
  color: ${theme.colors.black};
  border-radius: 50%;
  font-size: 10px;
  padding: 10px;
  height: 25px;
  width: 25px;
  svg {
    margin: 0;
    width: 12px;
    height: 12px;
  }
  transition: 0.2s;
  &:active,
  &:focus,
  &:hover {
    color: ${theme.colors.black};
    border: solid 1px ${theme.colors.black};
  }
`;

const SelectWrapper = styled.div`
  width: 100%;
  .ant-select {
    width: 100%;
    font-size: 16px;
  }
  & div.ant-select-selector {
    height: unset !important;
    padding: 5px 11px !important;
    border-radius: 5px !important;
  }
`;

const toVerb = (t: TFunction<'Host'>, type?: SlideType) => {
  if (!type) return '';
  if (type === 'wordcloud') return t('generate ideas');
  if (type === 'answers') return t('answer the question');
  if (type === 'aiAssessedAnswers') return t('answer the question');
  if (type === 'aiAssessedImageAnswers') return t('draw');
  if (type === 'canvas') return t('draw');
  if (type === 'voting') return t('vote');
  if (type === 'poll') return t('vote');
  return '';
};

const VoteTitle = ({
  round,
  onRoundChange,
  relatedRound,
  slateTitleColor,
}: {
  round: Slide;
  onRoundChange: (r: Slide) => void;
  relatedRound?: Slide;
  slateTitleColor?: string;
}) => {
  const { t } = useTranslation('Host');
  const metrics = useMetrics();
  const titleElement = round.elements.find(e => e.type === 'text');
  if (titleElement === undefined) return null;

  return (
    <StyledFormInputDiv>
      <p>
        {t('Vote title')}
        <Tooltip
          onVisibleChange={visible =>
            visible && metrics.logEvent('Creator.Tooltip.VoteTitle.Show')
          }
          title={t('What should your participants vote for?')}
        >
          <TooltipIconWrapper>
            <QuestionMarkSvg />
          </TooltipIconWrapper>
        </Tooltip>
      </p>
      <Input
        placeholder={`${t('Most relevant')} ${toSubjects(t, relatedRound?.type)}`}
        onChange={e => {
          const createdTextElement = createTextElement({
            textValue: e.target.value,
            textColor: slateTitleColor,
            lockedFromDeletion: true,
            lockedPosition: true,
            x: SLIDE_ASPECT.width * 0.05,
            y: SLIDE_ASPECT.height * 0.1,
            width: SLIDE_ASPECT.width * 0.9,
          });

          //the vote title elements is also present in contextElements with fixed Id. We need to update the id of the new element to match the existing one
          const existingTextElement = round.elements.find(e => e.type === 'text');
          if (existingTextElement) createdTextElement.id = existingTextElement.id;
          onRoundChange({
            ...round,
            elements: [createdTextElement],
          });
        }}
        value={parseSlateStringToText(titleElement.textValue)}
      />
    </StyledFormInputDiv>
  );
};

export const DurationSelector = ({
  round,
  onRoundChange,
  title,
}: {
  title: string;
  round: Slide;
  onRoundChange: (r: Slide) => void;
}) => {
  const { t } = useTranslation('Host');
  const metrics = useMetrics();
  const renderNumber = (number: number) => {
    if (number < 10) return '0' + number;
    return `${number}`;
  };
  const handleSetRoundDuration = useCallback(
    ({ seconds, minutes }: { seconds?: string; minutes?: string }) => {
      if (!round.durationSeconds) return;
      if (seconds !== undefined) {
        const parsedSeconds = Math.min(parseInt(seconds || '0'), 59);
        const currentSeconds = round.durationSeconds % 60;
        if (parsedSeconds < 0 || isNaN(parsedSeconds)) {
          setSecondsInputValue(renderNumber(currentSeconds));
          return;
        }
        const newDuration = round.durationSeconds - currentSeconds + parsedSeconds;
        onRoundChange({
          ...round,
          durationSeconds: Math.floor(newDuration) || 10,
          durationSecondsConfig: Math.floor(newDuration) || 10,
        });
        setSecondsInputValue(renderNumber(parsedSeconds));
        return;
      }
      if (minutes !== undefined) {
        const parsedMinutes = Math.min(parseInt(minutes || '0'), 999);
        const currentMinutes = Math.floor(round.durationSeconds / 60);
        if (parsedMinutes < 0 || isNaN(parsedMinutes)) {
          setMinutesInputValue(renderNumber(currentMinutes));
          return;
        }

        const newDuration =
          round.durationSeconds - currentMinutes * 60 + parsedMinutes * 60;
        onRoundChange({
          ...round,
          durationSeconds: Math.floor(newDuration),
          durationSecondsConfig: Math.floor(newDuration),
        });
        setMinutesInputValue(renderNumber(parsedMinutes));
        return;
      }
    },
    [onRoundChange, round],
  );
  const handleEditRoundDuration = useCallback(
    (editAmount: number) => {
      if (!round?.durationSeconds) {
        onRoundChange({
          ...round,
          durationSeconds: Math.min(Number(60)),
          durationSecondsConfig: Number(60),
        });
        return;
      }

      const newDuration = round.durationSeconds + editAmount;
      if (newDuration < 10) {
        onRoundChange({
          ...round,
          durationSeconds: Number(10),
          durationSecondsConfig: Number(10),
        });
      } else {
        onRoundChange({
          ...round,
          durationSeconds: Number(newDuration),
          durationSecondsConfig: Number(newDuration),
        });
      }

      setSecondsInputValue(renderNumber(Math.floor(newDuration % 60)));
      setMinutesInputValue(renderNumber(Math.floor(newDuration / 60)));
    },
    [onRoundChange, round],
  );

  const [secondsInputValue, setSecondsInputValue] = useState<string>(
    renderNumber(round.durationSeconds ? round.durationSeconds % 60 : 0),
  );
  const [minutesInputValue, setMinutesInputValue] = useState<string>(
    renderNumber(round.durationSeconds ? Math.floor(round?.durationSeconds / 60) : 1),
  );

  useEffect(() => {
    if (round.durationSeconds) {
      setSecondsInputValue(renderNumber(round.durationSeconds % 60));
      setMinutesInputValue(renderNumber(Math.floor(round?.durationSeconds / 60)));
    }
  }, [round]);

  return (
    <StyledFormInputDiv>
      <p style={{ marginTop: 0 }}>
        {t(title)}
        <Tooltip
          onVisibleChange={visible =>
            visible && metrics.logEvent('Creator.Tooltip.Duration.Show')
          }
          title={t('TheMaximumAmountOf', {
            defaultValue:
              'The maximum amount of time your participants will get to {{type}} this round',
            type: toVerb(t, round.type),
          })}
        >
          <TooltipIconWrapper>
            <QuestionMarkSvg />
          </TooltipIconWrapper>
        </Tooltip>
      </p>

      <TimerWrapper>
        <TimerButton
          type="ghost"
          onClick={() => {
            metrics.logEvent('LiveGame.AddTimeBeforePlay.Decrease.Clicked');
            if (!round.durationSeconds) {
              onRoundChange({
                ...round,
                durationSeconds: Number(60),
                durationSecondsConfig: Number(60),
              });
              return;
            }
            if (round.durationSeconds >= 300) {
              handleEditRoundDuration(-60);
              return;
            }
            if (round.durationSeconds > 60) {
              handleEditRoundDuration(-30);
              return;
            }
            if (round.durationSeconds > 15) handleEditRoundDuration(-10);
          }}
        >
          <IconWrapper>
            <MinusSvg />
          </IconWrapper>
        </TimerButton>
        {round.durationSeconds && (
          <TimerInputContainer>
            <TimerInput
              value={minutesInputValue}
              onChange={e => setMinutesInputValue(e.target.value)}
              onBlur={() => handleSetRoundDuration({ minutes: minutesInputValue })}
              bordered={false}
            />

            <span style={{ fontWeight: 'bold', fontSize: '30px', margin: '0 5px' }}>
              :
            </span>
            <TimerInput
              type="te"
              value={secondsInputValue}
              onBlur={() => handleSetRoundDuration({ seconds: secondsInputValue })}
              bordered={false}
              onChange={e => setSecondsInputValue(e.target.value)}
            />
          </TimerInputContainer>
        )}

        <TimerButton
          type="ghost"
          onClick={() => {
            metrics.logEvent('LiveGame.AddTimeBeforePlay.Increase.Clicked');
            if (!round.durationSeconds) {
              onRoundChange({
                ...round,
                durationSeconds: Number(60),
                durationSecondsConfig: Number(60),
              });
              return;
            }
            if (round.durationSeconds >= 300) {
              handleEditRoundDuration(+60);
              return;
            }
            if (round.durationSeconds >= 60) {
              handleEditRoundDuration(+30);
              return;
            }

            handleEditRoundDuration(+10);
          }}
        >
          <IconWrapper>
            <PlusSvg />
          </IconWrapper>
        </TimerButton>
      </TimerWrapper>
    </StyledFormInputDiv>
  );
};

const typeSelectorOptions = [
  'poll',
  'wordcloud',
  'canvas',
  'answers',
  'aiAssessedAnswers',
];

export const RoundTypeSelector = ({
  roundType,
  onRoundTypeSelected,
}: {
  roundType: SlideContentType | SlideType;
  onRoundTypeSelected: (type: SlideType | SlideContentType) => void;
}) => {
  const { t } = useTranslation('Host');
  const metrics = useMetrics();
  const activity = typeToActivityMap(roundType);
  const handleRoundTypeSelected = (roundType: string) => {
    onRoundTypeSelected(roundType as SlideType | SlideContentType);
  };

  if (!activity) return null;
  const activityMapToIcon = (icon?: string | null) => {
    if (!icon || icon === null) return undefined;
    switch (icon) {
      case 'CloudIcon':
        return <CloudSvg />;
      case 'SlideHeaderSvg':
        return <SlideHeaderSvg />;
      case 'DrawingsIcon':
        return <DrawingsSvg />;
      case 'FlashIcon':
        return <FlashSvg />;
      case 'PollIcon':
        return <PollSvg />;
      case 'YoutubeSvg':
        return <YoutubeSvg />;
      case 'VideoActivitySvg':
        return <VideoActivitySvg />;
      case 'QuestionsIcon':
        return <QuestionsSvg />;
      case 'InstantFeedbackIcon':
        return <InstantFeedbackSvg />;
      case 'InstantDrawingFeedbackIcon':
        return <AIImageFeedbackSvg />;
      case 'TitleImageSvg':
        return <TitleImageSvg />;
      case 'TextMediaSvg':
        return <SlideTextImageSvg />;
      case 'MediaTextSvg':
        return <SlideImageTextSvg />;
      case 'SlideHeaderText':
        return <SlideHeaderText />;
    }
  };
  const menu = (
    <Menu style={{ maxHeight: 300, overflowY: 'auto' }}>
      {typeSelectorOptions.map(roundTypeOpt => {
        const activity = typeToActivityMap(roundTypeOpt);

        const isSelected = roundType === roundTypeOpt;
        if (!activity) return null;

        return (
          <Menu.Item
            key={roundTypeOpt}
            className="roundTypeSelectorMenuItem"
            onClick={() => {
              handleRoundTypeSelected(roundTypeOpt);
            }}
            style={{
              background: isSelected ? '#c7d9d3' : undefined,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                height: 40,
              }}
            >
              <IconWrapper height="25px" width="25px">
                <>{activityMapToIcon(activity.icon)}</>
              </IconWrapper>
              <span
                style={{
                  fontWeight: 'bold',
                  paddingLeft: 15,
                }}
              >
                {t(activity.title)}
              </span>
              {activity.beta && <BetaFlag top={3} dropdown={true} />}
            </div>
          </Menu.Item>
        );
      })}
    </Menu>
  );
  return (
    <StyledFormInputDiv>
      <p style={{ marginTop: 0 }}>
        {t('Activity')}
        <Tooltip
          onVisibleChange={visible =>
            visible && metrics.logEvent('Creator.Tooltip.Activity.Show')
          }
          title={t('Select another activity by using the dropdown below')}
        >
          <TooltipIconWrapper>
            <QuestionMarkSvg />
          </TooltipIconWrapper>
        </Tooltip>
      </p>
      <Dropdown
        placement="bottomRight"
        trigger={['click']}
        overlay={menu}
        disabled={roundType === 'aiAssessedImageAnswers'} //TODO remove when we support switching activity type directly in the edit interactivity tab
      >
        <ActivityDropdownInitiator>
          <IconWrapper height="25px" width="25px" style={{ flex: 0 }}>
            {activityMapToIcon(activity.icon)}
          </IconWrapper>
          <span
            style={{
              fontWeight: 'bold',
              paddingLeft: 15,
              flex: 1,
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {activity.beta && <BetaFlag top={3} showRightSide={true} />}
            {t(activity.title)}
          </span>

          <IconWrapper
            height="12px"
            width="12px"
            color={'rgba(0, 0, 0, 0.25);'}
            style={{ flex: 0, transform: 'rotate(90deg)' }}
          >
            <ArrowRightSvg />
          </IconWrapper>
        </ActivityDropdownInitiator>
      </Dropdown>
    </StyledFormInputDiv>
  );
};

const ActivityDropdownInitiator = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  border-radius: 5px;
  border: 1px solid #d9d9d9;
  padding: 5px 11px;
  cursor: pointer;
  height: 42px;
`;

export const LimitResponsesSelector = ({
  round,
  onRoundChange,
}: {
  round: Slide;
  onRoundChange: (r: Slide) => void;
}) => {
  const metrics = useMetrics();
  const { t } = useTranslation('Host');
  return (
    <StyledFormInputDiv>
      <p style={{ marginTop: 0 }}>
        {t('Entries per participant')}
        <Tooltip
          onVisibleChange={visible =>
            visible && metrics.logEvent('Creator.Tooltip.limitResponses.Show')
          }
          title={t('MaximumNumber', {
            defaultValue:
              'The maximum number of {{subject}} your participants will get to submit this round',
            subject: toSubjects(t, round.type, true),
          })}
        >
          <TooltipIconWrapper>
            <QuestionMarkSvg />
          </TooltipIconWrapper>
        </Tooltip>
      </p>
      <SelectWrapper>
        <Select
          value={(round.responseLimit || 0)?.toString()}
          onChange={e => {
            metrics.logEvent('Creator.OnChange.LimitResponses');
            onRoundChange({
              ...round,
              responseLimit: Number(e),
            });
          }}
          suffixIcon={
            <IconWrapper
              height="12px"
              width="12px"
              style={{ transform: 'rotate(90deg)' }}
            >
              <ArrowRightSvg />
            </IconWrapper>
          }
        >
          <Option key={'0'} value={'0'}>
            {t('Unlimited')}
          </Option>
          <Option key={'1'} value={'1'}>
            1 {toSubjects(t, round.type)}
          </Option>
          <Option key={'2'} value={'2'}>
            2 {toSubjects(t, round.type, true)}
          </Option>
          <Option key={'3'} value={'3'}>
            3 {toSubjects(t, round.type, true)}
          </Option>
          <Option key={'4'} value={'4'}>
            4 {toSubjects(t, round.type, true)}
          </Option>
          <Option key={'5'} value={'5'}>
            5 {toSubjects(t, round.type, true)}
          </Option>
          <Option key={'6'} value={'6'}>
            6 {toSubjects(t, round.type, true)}
          </Option>
          <Option key={'7'} value={'7'}>
            7 {toSubjects(t, round.type, true)}
          </Option>
          <Option key={'8'} value={'8'}>
            8 {toSubjects(t, round.type, true)}
          </Option>
          <Option key={'9'} value={'9'}>
            9 {toSubjects(t, round.type, true)}
          </Option>
          <Option key={'10'} value={'10'}>
            10 {toSubjects(t, round.type, true)}
          </Option>
        </Select>
      </SelectWrapper>
    </StyledFormInputDiv>
  );
};

export const VotingInputs = ({
  relatedRounds,
  handleVotingRoundChanged,
}: {
  relatedRounds: Slide[];
  handleVotingRoundChanged: (round: Slide) => void;
}) => {
  const votingRound = relatedRounds.find(r => r.type === 'voting');
  const firstRound = relatedRounds[0];
  const questionElement = retrieveSlideQuestionElement(firstRound);
  if (!questionElement) return null;
  const questionSlate = parseStringToSlateObject(questionElement.textValue);
  const firstElement = questionSlate.find(e => e.type === 'paragraph');
  const firstChild = firstElement?.children?.[0];
  const slateTitleColor = firstChild
    ? isCuripodText(firstChild)
      ? firstChild?.color
      : firstElement?.color
    : firstElement?.color;
  if (!votingRound) return null;
  return (
    <>
      <VoteTitle
        onRoundChange={handleVotingRoundChanged}
        round={votingRound}
        slateTitleColor={slateTitleColor || theme.colors.black}
      />

      <DurationSelector
        title={'Vote duration'}
        onRoundChange={handleVotingRoundChanged}
        round={votingRound}
      />
    </>
  );
};

export const ResultPreview = ({
  roundType,
  votingEnabled = false,
}: {
  roundType: SlideType;
  votingEnabled?: boolean;
}) => {
  const { t } = useTranslation('Host');
  const metrics = useMetrics();

  const activity = typeToActivityMap(roundType);
  if (!activity) return null;

  return (
    <StyledFormInputDiv>
      <p style={{ marginTop: 0 }}>
        {t('Results')}
        <Tooltip
          onVisibleChange={visible =>
            visible && metrics.logEvent('Creator.Tooltip.ResultPreview.Show')
          }
          title={t('Read below how the results will be displayed')}
        >
          <TooltipIconWrapper>
            <QuestionMarkSvg />
          </TooltipIconWrapper>
        </Tooltip>
      </p>
      <RoundPreviewContainer>
        {activity && activity.alternateImage && (
          <ImageWrapper>
            {votingEnabled ? (
              <Image width={123} height={100} src={activity?.image.src} />
            ) : (
              <Image width={123} height={100} src={activity?.alternateImage.src} />
            )}
          </ImageWrapper>
        )}
        <ExplanationWrapper>
          <span style={{ fontSize: '14px', color: theme.colors.black }}>
            {t(activity.title)}
          </span>
          <p>
            {t(
              `${activity.title}_${
                !votingEnabled && !!activity?.alternateParagraph ? 'alternate' : 'default'
              }`,
              !votingEnabled && !!activity?.alternateParagraph
                ? activity?.alternateParagraph
                : activity?.paragraph,
            )}
          </p>
        </ExplanationWrapper>
      </RoundPreviewContainer>
    </StyledFormInputDiv>
  );
};

const ExplanationWrapper = styled.div`
  flex: 1;
  h3 {
    font-weight: bold;
    margin-bottom: 5px;
  }
  p {
    font-weight: 400;
    font-size: 14px;
    margin: 0;
  }
`;

const ImageWrapper = styled.div`
  flex: 1;
  margin-bottom: 10px;
  img {
    max-height: 100px;
    object-fit: fill;
  }
`;

const RoundPreviewContainer = styled.div`
  width: 100%;
`;
